import * as React from 'react';
import { Container, Content, BottomBar, Section, Timestamp } from '../../modules';
import { Resource, StampedModel, RestoreableModel, Model } from '../../services';

import { Button } from '@independent-software/typeui/controls/Button';
import { Dialog } from '@independent-software/typeui/controls/Dialog';
import { Loader } from '@independent-software/typeui/controls/Loader';
import { Icon } from '@independent-software/typeui/controls/Icon';
import { Message } from '@independent-software/typeui/controls/Message';
import { IAuth } from '../../AuthContext';
import { TRight } from '../../config/Config';

type TStep = 'loading' | 'loadError' | 'ready' | 'confirm' | 'deleting' | 'deleteError' | 'restoring' | 'restoreError' | 'error';

interface IViewerProps<T> {
  /** @ignore */
  className?: string;
  /** @ignore */
  children?: React.ReactNode;
  /** Unique ID of resource to load using Factory. */
  id: number;
  /** Factory to use to load resource. */
  factory: Resource<T>;
  /** Given a resource instance, this callback determines if it can be edited. */
  canEdit: ((item: any) => boolean) | TRight;
  /** Given a resource instance, this callback determines if it can be deleted or restored. */
  canDelete: ((item: any) => boolean) | TRight;
  /** Callback is called with a resource instance. It should return a view template. */
  content: (item: any) => React.ReactNode;
  /** Callback is called with a resource instance. It should return any controls
   *  to be shown in the bottom bar. */
  controls?: (item: any) => React.ReactNode;
  /** Message to show for deletion confirmation */
  confirmMessage?: React.ReactNode;
  item?: T;

  // Callbacks
  /** Callback is called when editing is requested. */
  onEdit: () => void;
  /** Callback is called after deletion. */
  onDelete: () => void;
  /** Callback is called after successful load. */
  onLoad?: (item: any) => void;
}

interface IViewerState {
  item: Model;
  step: TStep;
  error: any;
}

class Viewer<T extends Model> extends React.Component<IViewerProps<T> & { auth: IAuth }, IViewerState> {
  constructor(props: IViewerProps<T> & { auth: IAuth} ) {
    super(props);
    this.state = {
      item: null,
      step: 'loading',
      error: null
    };
  }

  componentDidMount = () => {
    this.loadItem();
  }

  componentDidUpdate = (prevProps: IViewerProps<T>) => {
    if(prevProps.item != this.props.item) this.setState({ item: this.props.item });
  }

  private loadItem = () => {
    this.props.factory.get(this.props.auth, this.props.id)
    .then((item) => {
      this.setState({
        step: 'ready',
        item: item
      });
      if(this.props.onLoad) this.props.onLoad(item);
    })
    .catch(error => {
      this.setState({
        step: 'loadError',
        error: error
      })
    });
  }

  private goto = (step:TStep) => {
    this.setState({ step: step});
  }  

  private handleEdit = () => {
    this.props.onEdit();
  }

  private handleCancelLoad = () => {
    this.setState({ step: 'error' });
  }

  private handleRetry = () => {
    this.setState({error: null, step: 'loading'});
    this.loadItem();
  }  

  private handleDelete = () => {
    this.setState({ step: 'deleting', error: null});
    this.state.item.$delete(this.props.auth)
      .then(_ => {
        this.props.onDelete();
      })
      .catch(error => {
        this.setState({
          step: 'deleteError',
          error: error
        })
      })
  }      

  private handleRestore = () => {
    this.setState({ step: 'restoring', error: null});
    this.state.item.$restore(this.props.auth)
      .then(_ => {
        this.loadItem();
      })
      .catch(error => {
        this.setState({
          step: 'restoreError',
          error: error
        })
      })
  }        

  private canEdit = () => {
    let p = this.props;
    // Trashed models cannot be edited.
    if(this.state.item instanceof RestoreableModel && (this.state.item as RestoreableModel).deleted_at != null) return false;
    if(typeof(p.canEdit) === 'string') return p.auth && p.auth.hasRight(p.canEdit);
    return p.canEdit(this.state.item);
  }

  private canDelete = () => {
    let p = this.props;
    // Trashed models cannot be deleted.
    if(this.state.item instanceof RestoreableModel && (this.state.item as RestoreableModel).deleted_at != null) return false;
    if(typeof(p.canDelete) === 'string') return p.auth && p.auth.hasRight(p.canDelete);
    return p.canDelete(this.state.item);
  }

  private canRestore = () => {
    let p = this.props;
    // Untrashed models cannot be restored.
    if(this.state.item instanceof RestoreableModel && (this.state.item as RestoreableModel).deleted_at == null) return false;    
    if(typeof(p.canDelete) === 'string') return p.auth && p.auth.hasRight(p.canDelete);
    return p.canDelete(this.state.item);
  }  

  render() {
    let p = this.props;
    return (
      <Container>
        {(this.state.step == 'loading' || this.state.step == 'loadError' || this.state.step == 'deleting') && 
          <Loader/>}
        {this.state.step != 'loading' && this.state.step != 'loadError' && this.state.step != 'error' && 
        <>
          <Content>
            {p.content(this.state.item)}
          </Content>
          <BottomBar>
            <div>
              {this.canEdit() && <Button primary onClick={this.handleEdit}><Icon name="edit"/> Edit</Button>}
              {p.controls && p.controls(this.state.item)}
            </div>
            <div>
              {this.canDelete() && <Button negative onClick={() => this.goto('confirm')}><Icon name="trash"/> Delete</Button>}
              {this.canRestore() && <Button secondary onClick={this.handleRestore}><Icon name="trash"/> Restore</Button>}
              {this.state.item instanceof StampedModel && <Timestamp model={this.state.item}/>}
            </div>
          </BottomBar>
        </>}
        {this.state.step == 'error' && 
        <Content>
          <Section padded>
            <Message type="error">Data not found.</Message>
          </Section>
        </Content>}        
        <Dialog.Xhr open={this.state.step == 'loadError'} error={this.state.error} onClose={this.handleCancelLoad} onRetry={this.handleRetry}/>    
        <Dialog.Xhr open={this.state.step == 'deleteError'} error={this.state.error} onClose={() => this.goto('ready')} onRetry={this.handleDelete}/>
        <Dialog.Xhr open={this.state.step == 'restoreError'} error={this.state.error} onClose={() => this.goto('ready')} onRetry={this.handleRestore}/>
        <Dialog.Confirm open={this.state.step == 'confirm'} onClose={() => this.goto('ready')} onConfirm={this.handleDelete}>
          {p.confirmMessage && <React.Fragment>{p.confirmMessage}</React.Fragment>}
          {!p.confirmMessage && <React.Fragment>You are about to delete this record. Are you sure?</React.Fragment>}
        </Dialog.Confirm>

      </Container>
    );
  }  
}

export { Viewer }
